/**
 * The Support area's reads: the support bot's knowledge base, a preview of its
 * search, and its activity and stats.
 *
 * Served by `worker/support-reads.ts` over the SUPPORT RPC binding rather than
 * the catalog, under the same rules: the URLs live here so
 * `api-requests.test.ts` can execute them against `SUPPORT_READ_ROUTES`.
 * Paging is limit/offset because that is the RPC's own vocabulary.
 */
import type { QueryClient, QueryFunctionContext } from "@tanstack/react-query";

import { apiGet } from "@/lib/api";
import { previewMacro, type PreviewSource } from "@/lib/support-macros";
import { AGGREGATE, DETAIL, LIVE_LIST } from "@/lib/query-freshness";
import type { UsersListResponse } from "@/types";
import type {
  TicketChatEntry,
  TicketChatPage,
  TicketTracePage,
} from "@/types-ticket-chat";
import type { TicketReplyDraft } from "@/types-ticket-drafts";
import type {
  SupportSecretsView,
  SupportSettingsView,
  TelegramStatusView,
  AiScenarioDetail,
  AiScenarioList,
  BlockedSenderList,
  ChatAvailabilityView,
  ChatHistoryPage,
  ChatSettingsView,
  InboxCounts,
  KnowledgeEntryDetail,
  KnowledgeList,
  MacroDetail,
  MacroList,
  MacroPreview,
  MacroVariable,
  MailSyncStatus,
  QuoteDetail,
  QuoteList,
  RequesterKind,
  SearchPreview,
  StatsDays,
  SupportActivity,
  SupportChatStats,
  SupportStats,
  TicketDetail,
  TicketList,
} from "@/types-support";

/** Rows per page on both Support tables. */
export const SUPPORT_PAGE_SIZE = 25;

export interface SupportKnowledgeListParams {
  /** "" for every kind. */
  kind: string;
  /** "" for every status. */
  status: string;
  search: string;
  page: number;
}

function offsetOf(page: number): number {
  return (Math.max(1, page) - 1) * SUPPORT_PAGE_SIZE;
}

export function supportKnowledgeListQuery(params: SupportKnowledgeListParams) {
  return {
    ...LIVE_LIST,
    queryKey: [
      "support-knowledge",
      params.kind,
      params.status,
      params.search,
      params.page,
    ] as const,
    queryFn: () =>
      apiGet<KnowledgeList>("/api/admin/support/knowledge", {
        kind: params.kind || undefined,
        status: params.status || undefined,
        query: params.search.trim() || undefined,
        limit: SUPPORT_PAGE_SIZE,
        offset: offsetOf(params.page),
      }),
  };
}

export function supportKnowledgeQuery(id: string) {
  return {
    ...DETAIL,
    queryKey: ["support-knowledge-entry", id] as const,
    queryFn: () =>
      apiGet<KnowledgeEntryDetail>(
        `/api/admin/support/knowledge/${encodeURIComponent(id)}`,
      ),
  };
}

export function supportSearchPreviewQuery(query: string) {
  return {
    ...DETAIL,
    queryKey: ["support-search-preview", query] as const,
    queryFn: () =>
      apiGet<SearchPreview>("/api/admin/support/search-preview", { query }),
  };
}

export function supportActivityQuery(params: { page: number }) {
  return {
    ...LIVE_LIST,
    queryKey: ["support-activity", params.page] as const,
    queryFn: () =>
      apiGet<SupportActivity>("/api/admin/support/activity", {
        limit: SUPPORT_PAGE_SIZE,
        offset: offsetOf(params.page),
      }),
  };
}

export function supportStatsQuery(days: StatsDays) {
  return {
    ...AGGREGATE,
    queryKey: ["support-stats", days] as const,
    queryFn: () => apiGet<SupportStats>("/api/admin/support/stats", { days }),
  };
}

// --- the inbox ---------------------------------------------------------------

/** Tickets per page in the inbox list. */
/** Fifteen rows a page (owner, 2026-10-16). */
export const INBOX_PAGE_SIZE = 15;

/** The list's status tabs; "open" is where the inbox opens. */
export const INBOX_TABS = ["open", "waiting", "solved", "spam", "all"] as const;
export type InboxTab = (typeof INBOX_TABS)[number];

/** "me" is resolved to the caller by the Worker; "none" is unassigned. */
export type InboxAssignee = "anyone" | "me" | "none";

/** How often the open count behind the sidebar chip is read again. */
export const INBOX_COUNTS_REFRESH_MS = 15_000;

/** "" is every channel. */
export type InboxChannel = "" | "email" | "form" | "chat";

/** The list's orders (nt-support TicketSort); "newest" is the default. */
export type InboxSort = "newest" | "oldest" | "channel" | "channel_desc";

/** "" is every requester; the rest are nt-support's requester kinds. */
export type InboxRequesterKind = "" | RequesterKind;

export interface SupportInboxListParams {
  tab: InboxTab;
  assignee: InboxAssignee;
  search: string;
  page: number;
  channel: InboxChannel;
  /** Only chats the AI is handling. */
  aiHandling: boolean;
  /** Only tickets whose newest non-note message is inbound. */
  awaiting: boolean;
  kind: InboxRequesterKind;
  /** "" is every tag. */
  tag: string;
  /** Omitted = "newest". */
  sort?: InboxSort;
}

/** What the inbox opens on: open tickets, anyone's, no search. */
export const INBOX_LANDING: SupportInboxListParams = {
  tab: "open",
  assignee: "anyone",
  search: "",
  page: 1,
  channel: "",
  aiHandling: false,
  awaiting: false,
  kind: "",
  tag: "",
  sort: "newest",
};

export function supportInboxListQuery(params: SupportInboxListParams) {
  const search = params.search.trim();
  return {
    ...LIVE_LIST,
    queryKey: [
      "support-inbox",
      params.tab,
      params.assignee,
      search,
      params.page,
      params.channel,
      params.aiHandling,
      params.awaiting,
      params.kind,
      params.tag,
      params.sort ?? "newest",
    ] as const,
    queryFn: () =>
      apiGet<TicketList>("/api/admin/support/inbox", {
        status: params.tab,
        channel: params.channel || undefined,
        aiHandling: params.aiHandling ? "true" : undefined,
        awaiting: params.awaiting ? "true" : undefined,
        kind: params.kind || undefined,
        tag: params.tag || undefined,
        sort: params.sort && params.sort !== "newest" ? params.sort : undefined,
        assigneeId: params.assignee === "anyone" ? undefined : params.assignee,
        // The support Worker searches from 2 characters; one is no filter yet.
        q: search.length >= 2 ? search : undefined,
        // Contact-form tickets from the staging site always show; the row
        // and the ticket page label them.
        includeStaging: "true",
        limit: INBOX_PAGE_SIZE,
        offset: (Math.max(1, params.page) - 1) * INBOX_PAGE_SIZE,
      }),
  };
}

export function supportInboxCountsQuery() {
  return {
    ...LIVE_LIST,
    refetchInterval: INBOX_COUNTS_REFRESH_MS,
    queryKey: ["support-inbox-counts"] as const,
    queryFn: () =>
      apiGet<InboxCounts>("/api/admin/support/inbox/counts", {
        includeStaging: "true",
      }),
  };
}

export function supportTicketQuery(id: string) {
  return {
    ...LIVE_LIST,
    queryKey: ["support-ticket", id] as const,
    queryFn: () =>
      apiGet<TicketDetail>(
        `/api/admin/support/inbox/${encodeURIComponent(id)}`,
      ),
  };
}

export function supportBlockedSendersQuery() {
  return {
    ...LIVE_LIST,
    queryKey: ["support-inbox-blocked"] as const,
    queryFn: () =>
      apiGet<BlockedSenderList>("/api/admin/support/inbox/blocked", {
        limit: 100,
      }),
  };
}

export function supportMailSyncStatusQuery() {
  return {
    ...LIVE_LIST,
    refetchInterval: 30_000,
    queryKey: ["support-inbox-sync-status"] as const,
    queryFn: () =>
      apiGet<MailSyncStatus>("/api/admin/support/inbox/sync-status"),
  };
}

/** Where an attachment downloads from: the Worker streams it with a fixed name. */
export function supportAttachmentHref(
  ticketId: string,
  messageId: string,
  attachmentId: string,
): string {
  return `/api/admin/support/inbox/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(messageId)}/${encodeURIComponent(attachmentId)}`;
}

// --- live chat ---------------------------------------------------------------

/** A chat ticket's rows from its chat object: the newest page; the socket adds the rest. */
export function supportChatHistoryQuery(ticketId: string) {
  return {
    ...LIVE_LIST,
    queryKey: ["support-chat", ticketId] as const,
    queryFn: () =>
      apiGet<ChatHistoryPage>(
        `/api/admin/support/inbox/${encodeURIComponent(ticketId)}/chat`,
        { limit: 50 },
      ),
  };
}

/** The 50 rows before `beforeSeq`, for "Load earlier messages" (`hasMore`). */
export function fetchEarlierChatRows(
  ticketId: string,
  beforeSeq: number,
): Promise<ChatHistoryPage> {
  return apiGet<ChatHistoryPage>(
    `/api/admin/support/inbox/${encodeURIComponent(ticketId)}/chat`,
    { beforeSeq, limit: 50 },
  );
}

/** My own "Available for chat" switch and who else is available. */
export function supportChatAvailabilityQuery() {
  return {
    ...LIVE_LIST,
    queryKey: ["support-chat-availability"] as const,
    queryFn: () =>
      apiGet<ChatAvailabilityView>("/api/admin/support/chat/availability"),
  };
}

export function supportChatSettingsQuery() {
  return {
    ...DETAIL,
    queryKey: ["support-chat-settings"] as const,
    queryFn: () => apiGet<ChatSettingsView>("/api/admin/support/chat/settings"),
  };
}

/** Support › Settings: every chat, email and Telegram setting with its source. */
export function supportSettingsQuery() {
  return {
    ...DETAIL,
    queryKey: ["support-settings"] as const,
    queryFn: () => apiGet<SupportSettingsView>("/api/admin/support/settings"),
  };
}

/** The Telegram bot: token and webhook secret set, getWebhookInfo, staff state. */
export function supportTelegramStatusQuery() {
  return {
    ...DETAIL,
    queryKey: ["support-settings", "telegram"] as const,
    queryFn: () =>
      apiGet<TelegramStatusView>("/api/admin/support/settings/telegram"),
  };
}

/** Which secrets the support Worker has (set or missing) and how to set each. */
export function supportSecretsQuery() {
  return {
    ...DETAIL,
    queryKey: ["support-settings", "secrets"] as const,
    queryFn: () =>
      apiGet<SupportSecretsView>("/api/admin/support/settings/secrets"),
  };
}

/**
 * The console admins of this environment, for the Telegram staff picker. The
 * write checks each picked id against the same read.
 */
export function supportAdminsQuery() {
  return {
    ...DETAIL,
    queryKey: ["support-settings", "admins"] as const,
    queryFn: () =>
      apiGet<UsersListResponse>("/api/admin/users", {
        page: 1,
        pageSize: 100,
        tab: "all",
        role: "admin",
      }),
  };
}

/** The chat AI scenarios: mode, published version, last test run. */
export function supportChatScenariosQuery() {
  return {
    ...DETAIL,
    queryKey: ["support-chat-scenarios"] as const,
    queryFn: () => apiGet<AiScenarioList>("/api/admin/support/chat/scenarios"),
  };
}

/** One scenario with its draft, versions, test run and promotion gate. */
export function supportChatScenarioQuery(id: string) {
  return {
    ...DETAIL,
    queryKey: ["support-chat-scenario", id] as const,
    queryFn: () =>
      apiGet<AiScenarioDetail>(
        `/api/admin/support/chat/scenarios/${encodeURIComponent(id)}`,
      ),
  };
}

export function supportChatStatsQuery(days: StatsDays) {
  return {
    ...AGGREGATE,
    queryKey: ["support-chat-stats", days] as const,
    queryFn: () =>
      apiGet<SupportChatStats>("/api/admin/support/chat/stats", { days }),
  };
}

// --- quotes ------------------------------------------------------------------

/** Quotes per page on the Quotes tab, as in the inbox. */
export const QUOTES_PAGE_SIZE = 30;

/** The Quotes tab's filters: awaiting approval is a draft. */
export const QUOTE_FILTERS = ["draft", "approved", "sent", "all"] as const;
export type QuoteFilter = (typeof QUOTE_FILTERS)[number];

export interface SupportQuotesListParams {
  filter: QuoteFilter;
  page: number;
}

export function supportQuotesListQuery(params: SupportQuotesListParams) {
  return {
    ...LIVE_LIST,
    queryKey: ["support-quotes", params.filter, params.page] as const,
    queryFn: () =>
      apiGet<QuoteList>("/api/admin/support/quotes", {
        status: params.filter === "all" ? undefined : params.filter,
        limit: QUOTES_PAGE_SIZE,
        offset: (Math.max(1, params.page) - 1) * QUOTES_PAGE_SIZE,
      }),
  };
}

export function supportQuoteQuery(id: string) {
  return {
    ...LIVE_LIST,
    queryKey: ["support-quote", id] as const,
    queryFn: () =>
      apiGet<QuoteDetail>(
        `/api/admin/support/quotes/${encodeURIComponent(id)}`,
      ),
  };
}

/** The PDF a draft would become, opened in the browser for review before approval. */
export function supportQuotePreviewHref(id: string): string {
  return `/api/admin/support/quotes/${encodeURIComponent(id)}/preview`;
}

/** Where an approved quote's PDF downloads from. */
export function supportQuotePdfHref(id: string): string {
  return `/api/admin/support/quotes/${encodeURIComponent(id)}/pdf`;
}

// --- macros ------------------------------------------------------------------

export interface SupportMacrosListParams {
  /** Rows to read; the table's page size when omitted. */
  limit?: number;
  search: string;
  /** "" for every category. */
  category: string;
  /** active, draft, archived or all. */
  status: string;
  /** "" for every channel. */
  channel: string;
  page: number;
}

/** What the Macros view opens on: active macros, no search. */
export const MACROS_LANDING: SupportMacrosListParams = {
  search: "",
  category: "",
  status: "active",
  channel: "",
  page: 1,
};

export function supportMacrosListQuery(params: SupportMacrosListParams) {
  const search = params.search.trim();
  return {
    ...LIVE_LIST,
    queryKey: [
      "support-macros",
      params.search,
      params.category,
      params.status,
      params.channel,
      params.page,
      params.limit ?? SUPPORT_PAGE_SIZE,
    ] as const,
    queryFn: () =>
      apiGet<MacroList>("/api/admin/support/macros", {
        // The support Worker searches from 2 characters; one is no filter yet.
        q: search.length >= 2 ? search : undefined,
        category: params.category || undefined,
        status: params.status || undefined,
        channel: params.channel || undefined,
        limit: params.limit ?? SUPPORT_PAGE_SIZE,
        offset:
          (Math.max(1, params.page) - 1) * (params.limit ?? SUPPORT_PAGE_SIZE),
      }),
  };
}

export function supportMacroQuery(id: string) {
  return {
    ...DETAIL,
    queryKey: ["support-macro", id] as const,
    queryFn: () =>
      apiGet<MacroDetail>(
        `/api/admin/support/macros/${encodeURIComponent(id)}`,
      ),
  };
}

/** The closed variable registry: it changes with a deploy, not between visits. */
export function supportMacroVariablesQuery() {
  return {
    ...AGGREGATE,
    queryKey: ["support-macro-variables"] as const,
    queryFn: () =>
      apiGet<MacroVariable[]>("/api/admin/support/macros/variables"),
  };
}

/**
 * A macro rendered against a company (and a ticket): a POST that changes
 * nothing, read like a query so the screens debounce, cache and retry it.
 */
export function supportMacroPreviewQuery(
  source: PreviewSource,
  context: {
    companyId: string | null;
    ticketId: string | null;
    /** A saved macro's version: part of the key only, so a saved edit never shows a cached older text. */
    version?: number | null;
  },
) {
  return {
    ...DETAIL,
    queryKey: [
      "support-macro-preview",
      source,
      context.companyId,
      context.ticketId,
      context.version ?? null,
    ] as const,
    queryFn: (): Promise<MacroPreview> =>
      previewMacro(source, {
        companyId: context.companyId,
        ticketId: context.ticketId,
      }),
  };
}

/** The drafts nt-support saved for a ticket; polled slowly, faster while a job runs. */
export const ticketDraftsQuery = (ticketId: string) => ({
  queryKey: ["ticket-reply-drafts", ticketId],
  queryFn: () =>
    apiGet<TicketReplyDraft[]>(
      `/api/admin/support/inbox/${encodeURIComponent(ticketId)}/drafts`,
    ),
  refetchInterval: 15_000,
  ...LIVE_LIST,
});

const CHAT_POLL_MS = 1_500;

export const ticketAgentChatPath = (ticketId: string) =>
  `/api/admin/support/inbox/${encodeURIComponent(ticketId)}/agent-chat`;

// Private on purpose: a cache touch goes through `ticketAgentChatQuery(id).queryKey`.
const ticketAgentChatKey = (ticketId: string) =>
  ["ticket-agent-chat", ticketId] as const;

/** By id, the newer copy of a row winning, oldest first. */
export function mergeChatEntries(
  current: readonly TicketChatEntry[],
  incoming: readonly TicketChatEntry[],
): TicketChatEntry[] {
  const byId = new Map(current.map((entry) => [entry.id, entry]));
  for (const entry of incoming) byId.set(entry.id, entry);
  return [...byId.values()].sort((a, b) => a.id - b.id);
}

/** Tickets whose next read must be the whole page (a row on screen changed). */
const wholePageNext = new WeakMap<QueryClient, Set<string>>();

/** The next read of this ticket's chat asks for every row, not only the newer ones. */
export function markChatReadWhole(client: QueryClient, ticketId: string) {
  const set = wholePageNext.get(client) ?? new Set<string>();
  set.add(ticketId);
  wholePageNext.set(client, set);
}

/**
 * The ticket's conversation with its agent. While a turn runs, or at a resting
 * poll of `restingPollMs`, a read asks for the newer rows only; a read on open,
 * on focus after a pause, or after `markChatReadWhole` asks for the whole page,
 * which is what refreshes a card someone pressed in Telegram.
 */
export function ticketAgentChatQuery(ticketId: string, restingPollMs?: number) {
  const queryKey = ticketAgentChatKey(ticketId);
  return {
    queryKey,
    queryFn: async ({ client }: QueryFunctionContext) => {
      const cached = client.getQueryData<TicketChatPage>(queryKey);
      const whole = wholePageNext.get(client)?.delete(ticketId) ?? false;
      const updatedAt = client.getQueryState(queryKey)?.dataUpdatedAt ?? 0;
      const polling =
        restingPollMs !== undefined &&
        Date.now() - updatedAt < restingPollMs * 1.5;
      const after =
        !whole && cached && (cached.working || polling)
          ? cached.entries.at(-1)?.id
          : undefined;
      const page = await apiGet<TicketChatPage>(ticketAgentChatPath(ticketId), {
        after,
      });
      return {
        ...page,
        entries: mergeChatEntries(cached?.entries ?? [], page.entries),
      };
    },
    refetchInterval: (query: { state: { data?: TicketChatPage } }) =>
      query.state.data?.working ? CHAT_POLL_MS : (restingPollMs ?? false),
    ...LIVE_LIST,
  };
}

export const ticketAgentTraceQuery = (ticketId: string) => ({
  queryKey: ["ticket-agent-trace", ticketId],
  queryFn: () =>
    apiGet<TicketTracePage>(
      `/api/admin/support/inbox/${encodeURIComponent(ticketId)}/agent-trace`,
    ),
  ...LIVE_LIST,
});
